import { COACH_ASSIGNED_LESSONS_QUERY } from '~/graphql/documents/coach';

/**
 * One piece of school work, as the page draws it.
 *
 * Everything below `completed` is the playlist behind the lesson, in the vocabulary every other
 * collection card in the app already speaks — because that is what the page draws it as. An empty
 * `imageUrl` is the honest answer for a playlist published without cover art, and the page has a
 * glyph card to fall back to rather than a picture-shaped hole.
 */
export interface CoachAssignedLesson {
  id: string;
  name: string;
  school?: string | null;
  completed: boolean;
  /**
   * Whether the work was pointed at this student — set for a group of theirs — rather than opened
   * to anyone at the school. It decides which outstanding section a card sits in.
   */
  assigned: boolean;
  imageUrl: string;
  completedCount: number;
  totalCount: number;
  /** Minutes of work left, not minutes the whole thing takes. */
  duration?: number | null;
  coins?: number | null;
}

/**
 * Where the last answer the server gave is kept, for the read below to fall back to and for a reader
 * that mounts again to start from.
 *
 * State rather than a module value because the two readers are two components and the fallback has
 * to be the same list for both — and because a module value written on the server would be a list
 * one student's request left behind for the next one's. It names the student it was read for, so a
 * different student signing in on the same tab starts from a read of their own.
 */
const HELD_STATE_KEY = 'coach-assigned-lessons-held';

interface HeldLessons {
  userId: string;
  lessons: CoachAssignedLesson[];
}

/**
 * The one read behind both places the student's school work shows up outside the chat.
 *
 * The page lists it and the nav item only asks whether there is any — and they mount together, on
 * every visit to the shell. So they share a `useAsyncData` key: Nuxt dedupes the request, the nav
 * gate costs nothing beyond the read the page was making anyway, and the two can never disagree
 * about whether the student has lessons.
 *
 * A dedicated boolean field on the server would be cheaper still for the nav on its own, but it
 * would be a second derivation of the same reach — and one that could answer "yes" to a nav item
 * whose page then renders empty. One read, two readers.
 *
 * The cost of one shared key is that the read is made once, when the first of the two mounts, and
 * the nav subscriber holds it for the whole visit — so nothing about a lesson finished mid-visit
 * reaches it on its own. `refresh` is how a reader that knows the list may have moved says so; the
 * cached list keeps painting while it is out, so asking is free.
 *
 * Which is why the read names its policy. Under urql's default the document cache answers a repeat
 * of a query it already holds without going near the server, and a refresh that never leaves the
 * browser is a refresh that corrects nothing — the student is left looking at the same stale list
 * under a line saying it has been updated. `cache-and-network` keeps the paint and makes the ask.
 *
 * A read that fails is a failure and says so, rather than answering with an empty list. The
 * difference is the whole of what the refresh costs: the list is already on screen when it is asked
 * for, and a revalidation that came back empty because a school database had a bad minute would
 * take the student's work off the page and the nav item with it. A list a minute out of date is
 * worth more than a list that is wrong.
 *
 * What it leaves on screen is {@see HELD_STATE_KEY}. `useAsyncData` does not leave `data` alone when
 * the handler throws — it puts `default()` back in — so the fallback is where "keep what we have"
 * has to live, and what it hands back is the last list the server actually answered with.
 *
 * On a first read there is no such list, and the failure does degrade to empty. That is deliberate:
 * the nav is chrome on every page in the app, and a bad minute is not a reason for the whole shell
 * to render an error — what it costs is a nav item, and the page behind it says what its empty
 * state says.
 *
 * The held list is also what a reader mounting again starts from, without a read. The bar mounts
 * on every public page a student opens, and Nuxt drops the entry when its last reader unmounts, so
 * without it every page would ask again. `/coach/lessons` is the reader that refreshes.
 */
export const useCoachAssignedLessons = () => {
  const { $urqlClient } = useNuxtApp();
  const { user } = useAuth();

  const held = useState<HeldLessons | null>(HELD_STATE_KEY, () => null);
  const heldForThisStudent = (): CoachAssignedLesson[] | undefined =>
    held.value && held.value.userId === user.value?.id ? held.value.lessons : undefined;

  const { data, pending, refresh } = useAsyncData(
    'coach-assigned-lessons',
    async (): Promise<CoachAssignedLesson[]> => {
      const result = await $urqlClient
        .query(COACH_ASSIGNED_LESSONS_QUERY, {}, { requestPolicy: 'cache-and-network' })
        .toPromise();

      if (result.error) {
        throw result.error;
      }

      const lessons = result.data?.core?.user?.self?.assignedLessons ?? [];
      held.value = user.value ? { userId: user.value.id, lessons } : null;

      return lessons;
    },
    {
      server: false,
      default: (): CoachAssignedLesson[] => heldForThisStudent() ?? [],
      getCachedData: (_key, _nuxtApp, { cause }) => (cause === 'initial' ? heldForThisStudent() : undefined),
    },
  );

  const lessons = computed<CoachAssignedLesson[]>(() => data.value ?? []);

  return {
    outstanding: computed(() => lessons.value.filter(lesson => !lesson.completed)),
    /** The outstanding work somebody set this student's group — the page's "to do" section. */
    assigned: computed(() => lessons.value.filter(lesson => !lesson.completed && lesson.assigned)),
    /** The outstanding work opened to the whole school — there to explore, never pushed. */
    explore: computed(() => lessons.value.filter(lesson => !lesson.completed && !lesson.assigned)),
    done: computed(() => lessons.value.filter(lesson => lesson.completed)),
    hasAny: computed(() => lessons.value.length > 0),
    /**
     * Waiting with nothing to show, which is not the same as waiting. A refresh keeps the list it
     * already has on screen — the read is a correction, and swapping a page of work for a spinner
     * every time one is asked for is the flicker the refresh was meant to avoid.
     */
    isLoading: computed(() => pending.value && lessons.value.length === 0),
    refresh,
  };
};
